import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  WORKSPACE_COLOR_ROLES, isHexColor, readWorkspaceColors, writeWorkspaceColors,
  clearWorkspaceColors,
} from '../utils/workspaceColors';

/**
 * The Configuration dialog: the customer's own colours for six chart elements.
 *
 * <h2>It edits a DRAFT and commits on Save</h2>
 *
 * Nothing reaches the chart until Save, and Cancel throws the draft away. A live-applying picker
 * sounds friendlier and is worse here: dragging through a colour wheel would fire a chart re-render
 * per pointer move over a series carrying thousands of marks, and there would be no way back to
 * where you started short of remembering six hex values.
 *
 * <p>The draft is re-read from storage every time the dialog OPENS rather than held across
 * openings, so a change made in another tab is what you see when you come back to this one.
 *
 * <h2>A native picker, not a component library one</h2>
 *
 * `input type="color"` is the browser's own: it is keyboard accessible, it is the dialog the
 * customer's OS already taught them, and it needs no dependency. Beside it is a text field, because
 * a customer matching a house style has a hex code in hand and a wheel is the wrong instrument for
 * pasting one.
 *
 * <h2>Empty means DEFAULT, and that is why there is no per-row reset</h2>
 *
 * Clearing the text field removes the stored choice, so the element goes back to the colour the
 * study owns. There is no such thing as a stored default here - only the absence of a choice - so a
 * reset button per row would be a second way to express the same state.
 */
const ConfigurationDialog = ({ open, onClose, defaults = {} }) => {
  const [draft, setDraft] = useState({});

  // Re-read on OPEN. Holding the draft across openings would show a stale palette after another tab
  // changed it, and would quietly re-commit that stale value on the next Save.
  useEffect(() => {
    if (open) setDraft(readWorkspaceColors());
  }, [open]);

  const groups = useMemo(() => {
    const out = [];
    WORKSPACE_COLOR_ROLES.forEach((role) => {
      const last = out[out.length - 1];
      if (last && last.group === role.group) last.roles.push(role);
      else out.push({ group: role.group, roles: [role] });
    });
    return out;
  }, []);

  /** What the swatch shows: the choice, else the study's own default, else something visible. */
  const shown = key => (isHexColor(draft[key]) ? draft[key] : (defaults[key] || '#888888'));

  const set = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    // The WHOLE role set is written, not only the touched keys, so a field a customer cleared is
    // removed rather than left at its previous value - see writeWorkspaceColors: anything that is
    // not a hex colour deletes the entry.
    const payload = {};
    WORKSPACE_COLOR_ROLES.forEach(({ key }) => { payload[key] = draft[key] ?? null; });
    writeWorkspaceColors(payload);
    onClose();
  };

  const handleResetAll = () => {
    clearWorkspaceColors();
    setDraft({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Configuration — chart colours</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2" sx={{ mb: 2 }}>
          Your choices are kept in this browser and take effect as soon as you save. Clear a field to
          put that element back to its standard colour.
        </DialogContentText>

        {groups.map(({ group, roles }, gi) => (
          <Box key={group} sx={{ mb: 2 }}>
            {gi > 0 && <Divider sx={{ mb: 2 }} />}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{group}</Typography>
            <Stack spacing={1.25}>
              {roles.map(({ key, label, hint }) => (
                <Stack key={key} direction="row" spacing={1.5} alignItems="center">
                  <Tooltip arrow title={hint}>
                    <Typography variant="body2" sx={{ minWidth: 150 }}>{label}</Typography>
                  </Tooltip>
                  {/* The browser's own picker: keyboard accessible, familiar, no dependency. */}
                  <input
                    type="color"
                    aria-label={`${group} ${label} colour`}
                    value={shown(key)}
                    onChange={e => set(key, e.target.value)}
                    style={{
                      width: 44, height: 30, padding: 0, border: '1px solid #bdbdbd',
                      borderRadius: 4, background: 'none', cursor: 'pointer',
                    }}
                  />
                  <TextField
                    size="small"
                    placeholder={defaults[key] || 'default'}
                    value={draft[key] ?? ''}
                    onChange={e => set(key, e.target.value)}
                    // Flagged rather than refused. A half-typed "#1a2" is a valid three-digit
                    // colour and "#1a2b" is not, so a field that rejected keystrokes would fight
                    // anyone typing a six-digit code.
                    error={Boolean(draft[key]) && !isHexColor(draft[key])}
                    helperText={Boolean(draft[key]) && !isHexColor(draft[key]) ? '#rgb or #rrggbb' : ' '}
                    sx={{ width: 140 }}
                    inputProps={{ 'aria-label': `${group} ${label} hex` }}
                  />
                  {!draft[key] && (
                    <Typography variant="caption" color="text.secondary">default</Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetAll} sx={{ mr: 'auto', textTransform: 'none' }}>
          Reset all to default
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none' }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfigurationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /**
   * The colour each element uses when the customer has not chosen one, by role key.
   *
   * <p>Passed IN rather than looked up here, because those defaults belong to the things that
   * draw with them — the PTA/PTB bands to `cycleColors`, the arrows to `UP_COLOR`, the RSI line
   * to `rsiLineDefault`. A dialog that held its own copy would show a swatch that disagreed with
   * the chart the moment one of them changed, and a role with NO entry falls through to the
   * `'#888888'` below — which is how the RSI swatch came to offer grey for a line drawing blue.
   */
  defaults: PropTypes.objectOf(PropTypes.string),
};

export default ConfigurationDialog;
